/**
 * Mixed Service Configuration Step Component
 * 
 * Second step for Mixed Service mode - allows user to configure
 * priority mode, weighting, transition time, and other fleet parameters
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  Slider,
  Divider,
  Alert,
  Stack,
  InputAdornment,
  Paper,
  Tooltip,
  Checkbox,
  Switch,
  Button,
} from '@mui/material';

import { ConfigurationStepProps, TripAnalysisResult } from './types';
import { logEvent } from '../../../../services/analytics';
import { analyzeTripsLayer } from './tripAnalysis';
import { Layer } from '../../../../context/context';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
/**
 * Mixed Service Configuration Step Props
 */
interface MixedServiceConfigurationStepProps extends ConfigurationStepProps {
  layers?: Layer[];
}

const MixedServiceConfigurationStep: React.FC<MixedServiceConfigurationStepProps> = ({
  config,
  onConfigChange,
  availablePoints,
  layers = [],
}) => {
  const { t } = useTranslation('mapWizards');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    priority: true,
    transition: false,
    vehicle: false,
    advanced: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Priority mode configuration
  const priorityMode = config.priorityMode || 'equal';
  const fixedRouteWeight = config.fixedRouteWeight ?? 50;
  const onDemandWeight = config.onDemandWeight ?? 50;
  const transitionTimeMinutes = config.transitionTimeMinutes ?? 0;

  // Set default priority mode if not set
  useEffect(() => {
    if (!config.priorityMode) {
      onConfigChange({ priorityMode: 'equal' });
    }
  }, [config.priorityMode, onConfigChange]);

  // Calculate trip analysis result if a trip layer is selected
  const tripAnalysisResult = React.useMemo<TripAnalysisResult | null>(() => {
    if (!config.selectedLayerId || layers.length === 0) {
      return null;
    }
    
    const selectedLayer = layers.find(l => l.layerId.toString() === config.selectedLayerId);
    if (!selectedLayer) {
      return null;
    }
    
    try {
      return analyzeTripsLayer(selectedLayer, config.tripsPerVehiclePerHour || 5);
    } catch (error) {
      console.error('Error analyzing trip layer for mixed fleet:', error);
      return null;
    }
  }, [config.selectedLayerId, config.tripsPerVehiclePerHour, layers]);

  // Auto-populate hourly vehicle requirements when trip layer is selected and demand-based allocation is enabled
  useEffect(() => {
    if (
      config.useDemandBasedAllocation &&
      tripAnalysisResult?.hourlyVehicleRequirements &&
      (!config.hourlyVehicleRequirements || 
       Object.values(config.hourlyVehicleRequirements).every(v => v === 5 || v === 0))
    ) {
      // Only auto-populate if current values are default (all 5s or all 0s)
      const hasDefaultValues = !config.hourlyVehicleRequirements || 
        Object.values(config.hourlyVehicleRequirements).every(v => v === 5 || v === 0);
      
      if (hasDefaultValues) {
        onConfigChange({
          hourlyVehicleRequirements: { ...tripAnalysisResult.hourlyVehicleRequirements }
        });
      }
    }
  }, [config.useDemandBasedAllocation, tripAnalysisResult, config.hourlyVehicleRequirements, onConfigChange]);

  // Handle priority mode change
  const handlePriorityModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMode = event.target.value as 'fixed-first' | 'on-demand-first' | 'equal' | 'weighted';
    onConfigChange({ priorityMode: newMode });
    
    logEvent({
      category: 'AutoFleetGenerator',
      action: 'PriorityModeChanged',
      label: 'Mixed Service',
      priority_mode: newMode,
    });
  };

  // Handle weight changes
  const handleFixedRouteWeightChange = (_event: Event, newValue: number | number[]) => {
    const weight = Array.isArray(newValue) ? newValue[0] : newValue;
    const onDemandWt = 100 - weight;
    onConfigChange({
      fixedRouteWeight: weight,
      onDemandWeight: onDemandWt,
    });
    
    logEvent({
      category: 'AutoFleetGenerator',
      action: 'WeightChanged',
      label: 'Mixed Service',
      fixed_weight: weight,
      on_demand_weight: onDemandWt,
    });
  };

  // Handle transition time change
  const handleTransitionTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const minutes = parseInt(event.target.value, 10) || 0;
    onConfigChange({ transitionTimeMinutes: minutes });
    
    logEvent({
      category: 'AutoFleetGenerator',
      action: 'TransitionTimeChanged',
      label: 'Mixed Service',
      minutes: minutes,
    });
  };

  // Validate weights
  const weightsValid = fixedRouteWeight + onDemandWeight === 100;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon size={20} name="gear" /> {t('autoFleetGen.mixedService.title')}
      </Typography>

      <Typography variant="body2" color="text.secondary" paragraph>
        {t('autoFleetGen.mixedService.description')}
      </Typography>

      <Stack spacing={2}>
        {/* Priority Mode Selection */}
        <Accordion expanded={expandedSections.priority} onChange={() => toggleSection('priority')}>
          <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="sliders" size={20} /> {t('autoFleetGen.mixedService.priority.title')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={priorityMode}
                onChange={handlePriorityModeChange}
              >
                <FormControlLabel
                  value="fixed-first"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('autoFleetGen.mixedService.priority.fixedFirstTitle')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('autoFleetGen.mixedService.priority.fixedFirstDescription')}
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  value="on-demand-first"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('autoFleetGen.mixedService.priority.onDemandFirstTitle')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('autoFleetGen.mixedService.priority.onDemandFirstDescription')}
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  value="equal"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('autoFleetGen.mixedService.priority.equalTitle')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('autoFleetGen.mixedService.priority.equalDescription')}
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  value="weighted"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('autoFleetGen.mixedService.priority.weightedTitle')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('autoFleetGen.mixedService.priority.weightedDescription')}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            {/* Weight Configuration (shown only when weighted mode is selected) */}
            {priorityMode === 'weighted' && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('autoFleetGen.mixedService.priority.weightHeader')}
                </Typography>

                <Box sx={{ px: 2 }}>
                  <Typography gutterBottom>
                    {t('autoFleetGen.mixedService.priority.weightSummary', { fixed: fixedRouteWeight, onDemand: onDemandWeight })}
                  </Typography>

                  <Slider
                    value={fixedRouteWeight}
                    onChange={handleFixedRouteWeightChange}
                    min={0}
                    max={100}
                    step={5}
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 50, label: '50%' },
                      { value: 100, label: '100%' },
                    ]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}%`}
                  />

                  {!weightsValid && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      {t('autoFleetGen.mixedService.priority.weightsInvalid')}
                    </Alert>
                  )}
                </Box>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Transition Time Configuration */}
        <Accordion expanded={expandedSections.transition} onChange={() => toggleSection('transition')}>
          <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon d={ICON_FALLBACKS.SwapHoriz} size={20} /> {t('autoFleetGen.mixedService.transition.title')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth
              label={t('autoFleetGen.mixedService.transition.label')}
              type="number"
              value={transitionTimeMinutes}
              onChange={handleTransitionTimeChange}
              helperText={t('autoFleetGen.mixedService.transition.helper')}
              InputProps={{
                endAdornment: <InputAdornment position="end">{t('autoFleetGen.mixedService.advanced.minutes')}</InputAdornment>,
              }}
              inputProps={{
                min: 0,
                max: 120,
              }}
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              {t('autoFleetGen.mixedService.transition.info')}
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* Vehicle Configuration */}
        <Accordion expanded={expandedSections.vehicle} onChange={() => toggleSection('vehicle')}>
          <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="bus" size={20} /> {t('autoFleetGen.mixedService.vehicle.title')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label={t('autoFleetGen.mixedService.vehicle.standardSeats')}
                type="number"
                value={config.standardSeats}
                onChange={(e) => onConfigChange({ standardSeats: parseInt(e.target.value, 10) || 0 })}
                inputProps={{ min: 1 }}
              />

              <TextField
                fullWidth
                label={t('autoFleetGen.mixedService.vehicle.wheelchairSeats')}
                type="number"
                value={config.wheelchairSeats}
                onChange={(e) => onConfigChange({ wheelchairSeats: parseInt(e.target.value, 10) || 0 })}
                inputProps={{ min: 0 }}
              />

              <FormControl fullWidth>
                <InputLabel>{t('autoFleetGen.mixedService.vehicle.startDepot')}</InputLabel>
                <Select
                  value={config.startLocationId}
                  onChange={(e) => onConfigChange({ startLocationId: e.target.value })}
                  label={t('autoFleetGen.mixedService.vehicle.startDepot')}
                  MenuProps={{ sx: { zIndex: 2600 } }}
                >
                  {availablePoints.map((point) => (
                    <MenuItem key={point.id} value={point.id}>
                      {point.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('autoFleetGen.mixedService.vehicle.endDepot')}</InputLabel>
                <Select
                  value={config.endLocationId}
                  onChange={(e) => onConfigChange({ endLocationId: e.target.value })}
                  label={t('autoFleetGen.mixedService.vehicle.endDepot')}
                  MenuProps={{ sx: { zIndex: 2600 } }}
                >
                  {availablePoints.map((point) => (
                    <MenuItem key={point.id} value={point.id}>
                      {point.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Advanced Options */}
        <Accordion expanded={expandedSections.advanced} onChange={() => toggleSection('advanced')}>
          <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="gear" size={20} /> {t('autoFleetGen.mixedService.advanced.title')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.enableShiftAllocation || false}
                    onChange={(e) => onConfigChange({ enableShiftAllocation: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      {t('autoFleetGen.mixedService.advanced.enableShift')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('autoFleetGen.mixedService.advanced.enableShiftHelper')}
                    </Typography>
                  </Box>
                }
              />

              {config.enableShiftAllocation && (
                <>
                  <TextField
                    fullWidth
                    label={t('autoFleetGen.mixedService.advanced.maxShiftTime')}
                    type="number"
                    value={config.maxShiftTimeMinutes}
                    onChange={(e) => onConfigChange({ maxShiftTimeMinutes: parseInt(e.target.value, 10) || 480 })}
                    helperText={t('autoFleetGen.mixedService.advanced.maxShiftTimeHelper')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{t('autoFleetGen.mixedService.advanced.minutes')}</InputAdornment>,
                    }}
                  />

                  <TextField
                    fullWidth
                    label={t('autoFleetGen.mixedService.advanced.breakLength')}
                    type="number"
                    value={config.breakLengthMinutes}
                    onChange={(e) => onConfigChange({ breakLengthMinutes: parseInt(e.target.value, 10) || 30 })}
                    helperText={t('autoFleetGen.mixedService.advanced.breakLengthHelper')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{t('autoFleetGen.mixedService.advanced.minutes')}</InputAdornment>,
                    }}
                  />

                  <TextField
                    fullWidth
                    label={t('autoFleetGen.mixedService.advanced.minBeforeBreak')}
                    type="number"
                    value={config.minTimeBeforeBreakMinutes}
                    onChange={(e) => onConfigChange({ minTimeBeforeBreakMinutes: parseInt(e.target.value, 10) || 120 })}
                    helperText={t('autoFleetGen.mixedService.advanced.minBeforeBreakHelper')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{t('autoFleetGen.mixedService.advanced.minutes')}</InputAdornment>,
                    }}
                  />

                  <TextField
                    fullWidth
                    label={t('autoFleetGen.mixedService.advanced.minAfterBreak')}
                    type="number"
                    value={config.minTimeAfterBreakMinutes}
                    onChange={(e) => onConfigChange({ minTimeAfterBreakMinutes: parseInt(e.target.value, 10) || 120 })}
                    helperText={t('autoFleetGen.mixedService.advanced.minAfterBreakHelper')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{t('autoFleetGen.mixedService.advanced.minutes')}</InputAdornment>,
                    }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={config.staggerBreaks || false}
                        onChange={(e) => onConfigChange({ staggerBreaks: e.target.checked })}
                      />
                    }
                    label={t('autoFleetGen.mixedService.advanced.staggerBreaks')}
                  />
                </>
              )}

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={config.useDemandBasedAllocation || false}
                    onChange={(e) => {
                      const isEnabled = e.target.checked;
                      
                    // Initialize hourly requirements if enabling and none exist
                    if (isEnabled && !config.hourlyVehicleRequirements) {
                      // If we have trip analysis results, use those; otherwise use default
                      if (tripAnalysisResult?.hourlyVehicleRequirements) {
                        onConfigChange({ 
                          useDemandBasedAllocation: true,
                          hourlyVehicleRequirements: { ...tripAnalysisResult.hourlyVehicleRequirements },
                        });
                      } else {
                        const baseCount = 5; // Default for mixed fleet
                        const defaultRequirements: Record<number, number> = {};
                        // Initialize all hours with the base vehicle count
                        for (let hour = 0; hour < 24; hour++) {
                          defaultRequirements[hour] = baseCount;
                        }
                        onConfigChange({ 
                          useDemandBasedAllocation: true,
                          hourlyVehicleRequirements: defaultRequirements,
                        });
                      }
                    } else {
                      onConfigChange({ useDemandBasedAllocation: isEnabled });
                    }
                      
                      logEvent({
                        category: 'AutoFleetGenerator',
                        action: 'DemandBasedAllocationToggled',
                        label: 'Mixed Service',
                        enabled: isEnabled ? 'Enabled' : 'Disabled',
                      });
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">{t('autoFleetGen.mixedService.advanced.demandBased')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('autoFleetGen.mixedService.advanced.demandBasedHelper')}
                    </Typography>
                  </Box>
                }
              />

              {config.useDemandBasedAllocation && (
                <>
                  <Alert severity="info" icon={<Icon size={20} d={ICON_FALLBACKS.TrendingUp} />}>
                    <Typography variant="body2">
                      {t('autoFleetGen.mixedService.advanced.demandBasedEnabledAlert')}
                    </Typography>
                  </Alert>

                  {/* Hourly Vehicle Requirements Editor */}
                  <Paper sx={{ p: 2, bgcolor: '#f9f9f9', border: '1px solid #e0e0e0' }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Icon name="bus" size={20} />
                          <Typography variant="subtitle2">{t('autoFleetGen.mixedService.advanced.hourlyTitle')}</Typography>
                        </Box>
                        <Box display="flex" gap={1}>
                          {tripAnalysisResult?.hourlyVehicleRequirements && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => {
                                if (tripAnalysisResult?.hourlyVehicleRequirements) {
                                  onConfigChange({ 
                                    hourlyVehicleRequirements: { ...tripAnalysisResult.hourlyVehicleRequirements }
                                  });
                                }
                              }}
                            >
                              {t('autoFleetGen.mixedService.advanced.resetToTripSet')}
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const baseCount = 5;
                              const requirements: Record<number, number> = {};
                              for (let hour = 0; hour < 24; hour++) {
                                requirements[hour] = baseCount;
                              }
                              onConfigChange({ hourlyVehicleRequirements: requirements });
                            }}
                          >
                            {t('autoFleetGen.mixedService.advanced.resetAllTo', { value: 5 })}
                          </Button>
                        </Box>
                      </Box>

                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                      {tripAnalysisResult
                        ? t('autoFleetGen.mixedService.advanced.hourlyDescriptionAnalysis')
                        : t('autoFleetGen.mixedService.advanced.hourlyDescription')}
                    </Typography>

                    {/* Visual Preview */}
                    {config.hourlyVehicleRequirements && (
                      <Box sx={{ mb: 2, p: 2, bgcolor: '#fff', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                          {t('autoFleetGen.mixedService.advanced.visualPreview')}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 0.5,
                            height: 60,
                            borderBottom: '1px solid #ddd',
                            pb: 0.5,
                          }}
                        >
                          {Array.from({ length: 24 }, (_, hour) => {
                            const value = config.hourlyVehicleRequirements?.[hour] ?? 0;
                            const maxValue = Math.max(
                              ...Object.values(config.hourlyVehicleRequirements || {}),
                              1
                            );
                            const heightPercent = maxValue > 0 ? (value / maxValue) * 100 : 0;
                            
                            return (
                              <Box
                                key={hour}
                                sx={{
                                  flex: 1,
                                  height: `${heightPercent}%`,
                                  bgcolor: value > 0 ? '#1976d2' : '#e0e0e0',
                                  borderRadius: '2px 2px 0 0',
                                  minHeight: value > 0 ? 4 : 2,
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: value > 0 ? '#1565c0' : '#d0d0d0',
                                    transform: 'scaleY(1.1)',
                                  },
                                }}
                                title={`${hour.toString().padStart(2, '0')}:00 - ${value} vehicle${value !== 1 ? 's' : ''}`}
                              />
                            );
                          })}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">00:00</Typography>
                          <Typography variant="caption" color="text.secondary">12:00</Typography>
                          <Typography variant="caption" color="text.secondary">23:00</Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Hourly Requirements Grid */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(8, 1fr)',
                        gap: 1,
                      }}
                    >
                      {Array.from({ length: 24 }, (_, hour) => {
                        const currentValue = config.hourlyVehicleRequirements?.[hour] ?? 0;
                        
                        return (
                          <TextField
                            key={hour}
                            label={`${hour.toString().padStart(2, '0')}:00`}
                            type="number"
                            size="small"
                            value={currentValue}
                            onChange={(e) => {
                              const value = parseInt(e.target.value, 10);
                              if (!isNaN(value) && value >= 0) {
                                onConfigChange({
                                  hourlyVehicleRequirements: {
                                    ...(config.hourlyVehicleRequirements || {}),
                                    [hour]: value,
                                  },
                                });
                              }
                            }}
                            inputProps={{ min: 0 }}
                            sx={{
                              '& .MuiInputBase-input': {
                                textAlign: 'center',
                                fontWeight: currentValue > 0 ? 600 : 400,
                                color: currentValue > 0 ? 'primary.main' : 'text.secondary',
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Paper>
                </>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Configuration Summary */}
        <Paper elevation={1} sx={{ p: 2, backgroundColor: 'action.selected' }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('autoFleetGen.mixedService.summary.title')}
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              <strong>{t('autoFleetGen.mixedService.summary.priorityMode')}</strong> {
                priorityMode === 'fixed-first' ? t('autoFleetGen.mixedService.summary.modeFixedFirst') :
                priorityMode === 'on-demand-first' ? t('autoFleetGen.mixedService.summary.modeOnDemandFirst') :
                priorityMode === 'equal' ? t('autoFleetGen.mixedService.summary.modeEqual') :
                t('autoFleetGen.mixedService.summary.modeWeighted')
              }
            </Typography>
            {priorityMode === 'weighted' && (
              <Typography variant="body2">
                <strong>{t('autoFleetGen.mixedService.summary.weights')}</strong> {t('autoFleetGen.mixedService.summary.weightsValue', { fixed: fixedRouteWeight, onDemand: onDemandWeight })}
              </Typography>
            )}
            <Typography variant="body2">
              <strong>{t('autoFleetGen.mixedService.summary.transitionTime')}</strong> {t('autoFleetGen.mixedService.summary.transitionTimeValue', { value: transitionTimeMinutes })}
            </Typography>
            <Typography variant="body2">
              <strong>{t('autoFleetGen.mixedService.summary.vehicleLabel')}</strong> {t('autoFleetGen.mixedService.summary.vehicleValue', { standard: config.standardSeats, wheelchair: config.wheelchairSeats })}
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default MixedServiceConfigurationStep;

